import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader, StatusBadge, Empty } from "@/components/shared";
import { fmtNOK, CUSTOMER_STATUS, KYC_STATUS } from "@/lib/labels";
import { Progress } from "@/components/ui/progress";
import { UserPlus } from "lucide-react";

const STEP_ORDER = ["invitert", "under_utfylling", "innsendt", "under_kontroll", "godkjent"];

export default function Onboarding() {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    Promise.all([
      api.get("/customers?status=onboarding"),
      api.get("/customers?status=intern_godkjenning"),
      api.get("/customers?status=klar_signering"),
    ]).then((res) => setRows(res.flatMap((r) => r.data)));
  }, []);

  return (
    <Page>
      <PageHeader title="Onboarding" subtitle="Kunder under registrering, KYC og godkjenning" testid="onboarding-header" />
      {rows.length > 0 && (() => {
        const totalMrr = rows.reduce((a, c) => a + (c.mrr || 0), 0);
        const totalArr = rows.reduce((a, c) => a + (c.arr || (c.mrr || 0) * 12), 0);
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="onboarding-summary">
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="label-caps">Kunder under onboarding</p>
              <p className="font-display text-2xl mt-2" data-testid="onboarding-total-count">{rows.length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="label-caps">Total MRR (samlet)</p>
              <p className="font-display text-2xl mt-2 text-emerald-400" data-testid="onboarding-total-mrr">{fmtNOK(totalMrr)}<span className="text-sm text-muted-foreground font-normal">/mnd</span></p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="label-caps">Total ARR (samlet)</p>
              <p className="font-display text-2xl mt-2" data-testid="onboarding-total-arr">{fmtNOK(totalArr)}</p>
            </div>
          </div>
        );
      })()}
      {rows.length === 0 ? <Empty icon={UserPlus} title="Ingen kunder under onboarding" hint="Konverter et lead for å starte." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((c) => {
            const idx = Math.max(0, STEP_ORDER.indexOf(c.kyc_status));
            const pct = c.status === "aktiv" ? 100 : ((idx + 1) / (STEP_ORDER.length + 1)) * 100;
            return (
              <button key={c.id} onClick={() => nav(`/kunder/${c.id}`)} data-testid={`onboarding-card-${c.id}`}
                className="bg-card border border-border rounded-xl p-5 text-left hover:border-white/30 transition-colors">
                <div className="flex items-center justify-between">
                  <p className="font-medium">{c.legal_name}</p>
                  <StatusBadge map={CUSTOMER_STATUS} value={c.status} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{c.org_number}</p>
                <div className="mt-4"><Progress value={pct} className="h-1.5" /></div>
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span>KYC: {KYC_STATUS[c.kyc_status]}</span>
                  <span>{fmtNOK(c.mrr)}/mnd</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Page>
  );
}
